import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { postsApi } from '../services/api';
import PostCard from '../components/PostCard/PostCard';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import { useAuth } from '../context/AuthContext';
import './HashtagFeed.css';

const HashtagFeed = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { hashtag, feedType: routeFeedType, tagToSearchFor } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');

  const searchParams = new URLSearchParams(location.search);
  const normalizedHashtag = (tagToSearchFor || hashtag || '').replace(/^#+/, '');
  const feedType = (routeFeedType || searchParams.get('feedType') || 'flex').toLowerCase();
  const isFeedScopedTagRoute = Boolean(routeFeedType || tagToSearchFor || searchParams.get('feedType'));
  const filter = ['all', 'short', 'long'].includes(searchParams.get('filter'))
    ? searchParams.get('filter')
    : 'all';
  const LIMIT = 20;

  const { user } = useAuth();

  const normalizePost = (post) => ({
    ...post,
    author: post?.author || '',
    permlink: post?.permlink || '',
    body: typeof post?.body === 'string' ? post.body : '',
    parent_author: post?.parent_author || post?.parentAuthor || '',
    depth: Number.isFinite(post?.depth) ? post.depth : 0
  });

  const fetchPostsByHashtag = useCallback(async () => {
    setLoading(true);
    setError('');
    setOffset(0);
    try {
      const response = isFeedScopedTagRoute
        ? await postsApi.getFeedPostsByTag(feedType, normalizedHashtag, LIMIT, 0, user?.username)
        : await postsApi.getPostsByTag(normalizedHashtag, LIMIT, 0, user?.username);
      
      const safePosts = Array.isArray(response?.posts)
        ? response.posts.map(normalizePost).filter((p) => p.author && p.permlink)
        : [];
      setPosts(safePosts);
      setHasMore(response?.hasMore === true);
      setTotal(Number.isFinite(response?.total) ? response.total : safePosts.length);
      setOffset(LIMIT);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError(`Failed to load posts: ${err.message}`);
      setHasMore(false);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [LIMIT, feedType, isFeedScopedTagRoute, normalizedHashtag, user?.username]);

  useEffect(() => {
    fetchPostsByHashtag();
  }, [fetchPostsByHashtag]);

  const loadMorePosts = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    setError('');
    try {
      const response = isFeedScopedTagRoute
        ? await postsApi.getFeedPostsByTag(feedType, normalizedHashtag, LIMIT, offset, user?.username)
        : await postsApi.getPostsByTag(normalizedHashtag, LIMIT, offset, user?.username);

      const safePosts = Array.isArray(response?.posts)
        ? response.posts.map(normalizePost).filter((p) => p.author && p.permlink)
        : [];

      setPosts((prev) => [...prev, ...safePosts.filter((post) => !prev.some((existing) => existing.author === post.author && existing.permlink === post.permlink))]);
      setHasMore(response?.hasMore === true);
      setTotal(Number.isFinite(response?.total) ? response.total : (offset + safePosts.length));
      setOffset((prev) => prev + LIMIT);
    } catch (err) {
      console.error('Error loading more tagged posts:', err);
      setError(`Failed to load more posts: ${err.message}`);
    } finally {
      setLoadingMore(false);
    }
  }, [LIMIT, feedType, hasMore, isFeedScopedTagRoute, loadingMore, normalizedHashtag, offset, user?.username]);

  const handleFilterChange = useCallback((nextFilter) => {
    const nextSearchParams = new URLSearchParams(location.search);
    if (nextFilter === 'all') {
      nextSearchParams.delete('filter');
    } else {
      nextSearchParams.set('filter', nextFilter);
    }
    navigate({ pathname: location.pathname, search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : '' }, { replace: false });
  }, [location.pathname, location.search, navigate]);

  const getFilteredPosts = () => {
    if (!Array.isArray(posts)) return [];
    if (filter === 'all') return posts;
    
    return posts.filter(post => {
      const bodyText = typeof post.body === 'string' ? post.body : '';
      const isShortPost = post.depth > 0 || !!post.parent_author || bodyText.length < 500;
      if (filter === 'short') return isShortPost;
      if (filter === 'long') return !isShortPost;
      return true;
    });
  };

  const filteredPosts = getFilteredPosts();

  if (loading) {
    return (
      <>
        <SidebarHeader title={`#${normalizedHashtag}`} onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <div className="hashtag-feed">
          <div className="hashtag-header">
            <h1>#{normalizedHashtag}</h1>
          </div>
          <div className="loading-container">
            <LoadingSpinner />
            <p className="loading-text">Searching for posts...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SidebarHeader title={`#${normalizedHashtag}`} onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <div className="hashtag-feed">
      <div className="hashtag-header">
        <h1>#{normalizedHashtag}</h1>
        <p className="post-count">{filter === 'all' ? total || filteredPosts.length : filteredPosts.length} posts</p>
      </div>

      <div className="filter-controls">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All Posts
        </button>
        <button 
          className={`filter-btn ${filter === 'short' ? 'active' : ''}`}
          onClick={() => handleFilterChange('short')}
        >
          Short Posts
        </button>
        <button 
          className={`filter-btn ${filter === 'long' ? 'active' : ''}`}
          onClick={() => handleFilterChange('long')}
        >
          Long Posts
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="posts-container">
        {filteredPosts.length > 0 ? (
          filteredPosts.map(post => (
            <PostCard 
              key={`${post.author}-${post.permlink}`}
              post={post}
              showActions={true}
            />
          ))
        ) : (
          <div className="no-posts">
            <p>No {filter !== 'all' ? filter : ''} posts found with this hashtag.</p>
          </div>
        )}
      </div>

      {!loading && posts.length > 0 && hasMore && (
        <div className="hashtag-load-more">
          <button className="hashtag-load-more-btn" onClick={loadMorePosts} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : `Load More (${posts.length} posts loaded)`}
          </button>
        </div>
      )}

      {!loading && posts.length > 0 && !hasMore && (
        <div className="hashtag-end-message">
          <p>No more posts to load</p>
        </div>
      )}
      </div>
    </>
  );
};

export default HashtagFeed;
